import { useState, useMemo, useEffect, useRef } from 'react';
import { BsEye } from 'react-icons/bs';
import { IoChatboxEllipsesOutline, IoDocumentTextOutline } from 'react-icons/io5';
import { MdOutlineKeyboardBackspace } from 'react-icons/md';
import { useHistory, useParams } from 'react-router-dom';
import * as Sentry from "@sentry/react";
import ChatImageViewerBase from '../../../chat-session-info/ChatImageViewerBase';
import useDialogOpenState from '@minohealth/common/hooks/useDialogOpenState';
import { useSelector } from 'react-redux';
import LLMApiService from "@minohealth/common/services/LLMApiService";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { getSpacesPresignedUrl } from '@minohealth/common/services/SpacesService';

import { Button } from "@/components/ui/button";

import "./style.css";

dayjs.extend(relativeTime);

// ScanImage component to handle individual scan thumbnail loading
const ScanImage = ({ thumbnailUrl }) => {
  const { user } = useSelector((state) => state.app.datastate);
  const [imageUrl, setImageUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!thumbnailUrl) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    getSpacesPresignedUrl({ objectUrl: thumbnailUrl, userToken: user?.userToken })
      .then(presignedUrl => {
        if (!cancelled) setImageUrl(presignedUrl);
      })
      .catch(error => {
        Sentry.captureException(error, {
          extra: { context: 'Study - ScanImage loadThumbnail', thumbnailUrl }
        });
        console.error("Error loading scan thumbnail:", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [thumbnailUrl, user?.userToken]);

  return (
    <div className="study-scan__image-container">
      {isLoading ? (
        <div className="study-scan__image-placeholder">
          <div className="study-scan__image-skeleton">
            <div className="skeleton-pulse"></div>
          </div>
        </div>
      ) : imageUrl ? (
        <img 
          src={imageUrl}
          alt="Scan thumbnail" 
          className="study-scan__image" 
        />
      ) : (
        <div className="study-scan__image-placeholder">
          <span>No image available</span>
        </div>
      )}
    </div>
  );
};

// New: SeriesCard component
const SeriesCard = ({ series, onClick }) => {
  // Use the first scan in the series for preview/summary
  const firstScan = series[0];
  return (
    <div className="series-card" onClick={onClick}>
      <div className="series-card__image-container">
        <ScanImage thumbnailUrl={firstScan.image?.thumbnailUrl} />
      </div>
      <div className="series-card__info">
        <h4 className="series-card__title">{firstScan.seriesInfo?.seriesDescription || 'Series'}</h4>
        <div className="series-card__details">
          <div><span>Series Instance UID:</span> {firstScan.seriesInfo?.seriesInstanceUid}</div>
          <div><span>Modality:</span> {firstScan.modality}</div>
          <div><span>Series Number:</span> {firstScan.seriesInfo?.seriesNumber}</div>
          <div><span>Number of Instances:</span> {series.length}</div>
        </div>
      </div>
    </div>
  );
};

function Study() {
  // Get the study ID from URL parameters
  const history = useHistory();
  const { user } = useSelector((state) => state.app.datastate);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [singleStudyData, setSingleStudyData] = useState();
  
  const { studyInstanceUid } = useParams();
  const apiService = useMemo(() => {
    return user?.userToken ? LLMApiService(user.userToken) : null;
  }, [user?.userToken]);

  const [selectedSeriesUid, setSelectedSeriesUid] = useState(null);

  // Group scans by studyInstanceUid (series)
  const seriesMap = useMemo(() => {
    if (!singleStudyData) return {};
    const map = {};
    (singleStudyData?.items ?? []).forEach(scan => {
      const uid = scan.seriesInfo?.seriesInstanceUid;
      if (!uid) return;
      if (!map[uid]) map[uid] = [];
      map[uid].push(scan);
    });
    return map;
  }, [singleStudyData]);

  // List of all series
  const allSeries = useMemo(() => Object.values(seriesMap), [seriesMap]);

  // If a series is selected, show only its instances
  const scansToShow = useMemo(() => {
    if (!selectedSeriesUid) return null;
    return seriesMap[selectedSeriesUid] || [];
  }, [selectedSeriesUid, seriesMap]);

  async function getStudy() {
    // Don't fetch if we don't have the required data
    if (!apiService || !user?.currentSession) {
      setError("Missing authentication or session data");
      return;
    }
    
    // Prevent duplicate requests while loading
    if (isLoading) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const { data } = await apiService.getStudy(studyInstanceUid);
      
      setSingleStudyData(data?.data ?? data);
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          context: 'Study - getStudy API call',
          studyInstanceUid: studyInstanceUid || 'undefined',
          userToken: user?.userToken ? 'present' : 'missing',
          userId: user?._id || 'undefined',
          currentSession: user?.currentSession || 'undefined',
          apiServiceAvailable: !!apiService
        }
      });
      console.error("Failed to fetch studies:", error);
      setError(error.message || "Failed to fetch studies");
      // Set empty array instead of undefined for better UX
      setSingleStudyData([]);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getStudy();
  }, [studyInstanceUid, user?.currentSession, user?.userToken]);
  
  // In a real application, you would fetch the specific study data using the studyId
  


  function handleOpenReport(reportId) {
    history.push(`/anokye/report-overview/${reportId}`);
  }

  function handleSeriesClick(uid) {
    setSelectedSeriesUid(uid);
  }

  function handleBackToSeries() {
    setSelectedSeriesUid(null);
  }

  function handleBackToStudies() {
    history.push('/anokye/report-history');
  }

  const imageViewerRef = useRef(null);
  const [_, setIsImageViewerOpen] = useDialogOpenState(imageViewerRef)

  function openImageViewer() {
    setIsImageViewerOpen(true)
  }

  return (
    <div className="study-page">
      {/* Top Section: Patient Info */}
      <header className="study-page__header">
        <div className="study-page__patient-section">
          <div className="study-page__patient-info">
            <div className="study-page__label">Patient</div>
            <h2 className="study-page__patient-name">{singleStudyData?.patient?.patientName?.replace(/\^/g, " ")?.toUpperCase()}</h2>
          </div>
          <div className="study-page__date">
            <div className="study-page__date-text">{dayjs(singleStudyData?.studyDate).format('dddd, MMMM D, YYYY')}</div>
          </div>
        </div>
      </header>

      <main className="study-page__content">
        <div className="study-page__meta-section">
          {/* Left column: Study Details */}
          <div className="study-page__details">
            <div className="study-page__label">Study</div>
            <h3 className="study-page__detail-name">{singleStudyData?.studyDescription}</h3>
            
            <div className="study-page__detail-item">
              <span>Study Date:</span> {dayjs(singleStudyData?.studyDate).format('dddd, MMMM D, YYYY')}
            </div>
            {/* Accession Number not provided in new structure */}
            <div className="study-page__detail-item">
              <span>Modality:</span> {singleStudyData?.modality?.toUpperCase()}
            </div>
            <div className="study-page__detail-item">
              <span>Study ID:</span> {singleStudyData?.studyId}
            </div>

            {/* <div className="study-page__detail-item">
              <span>Referring Physician Name:</span> {singleStudyData?.at(0)?.studyInfo?.referringPhysician}
            </div>
            <div className="study-page__detail-item">
              <span>Study ID:</span> {singleStudyData?.at(0)?.studyInfo?._id}
            </div>
            <div className="study-page__detail-item">
              <span>Requesting Physician:</span> {singleStudyData?.at(0)?.studyInfo?.requestingPhysician}
            </div>
            <div className="study-page__detail-item">
              <span>Requested Procedure Description:</span> {singleStudyData?.at(0)?.studyInfo?.requestedProcedure}
            </div> */}
            
            <div className="study-page__actions">
              <button
                className="study-page__action-btn study-page__report-btn"
                onClick={openImageViewer}
              >
              <BsEye className="study-page__btn-icon" />
              <span>View Scan(s)</span>
              </button>
              {/* <button className="study-page__action-btn study-page__share-btn">
                <FiShare2 className="study-page__btn-icon" />
                <span>Share</span>
              </button> */}
            </div>

            {singleStudyData != null && (
              <ChatImageViewerBase
                srcImage={singleStudyData?.items?.[0]?.image}
                onCloseViewer={() => setIsImageViewerOpen(false)}
                ref={imageViewerRef}
              />
            )}
          </div>

          {/* Right column: Scans or Series */}
          <div className="study-page__scans">
            {/* If no series selected, show all series cards */}
            {!selectedSeriesUid && (
              <>
                <button className="series-back-btn" onClick={handleBackToStudies}>
                  <MdOutlineKeyboardBackspace />
                  Back to Studies
                </button>
                {allSeries.map((series, idx) => (
                  <SeriesCard
                    key={series[0]?.seriesInfo?.seriesInstanceUid || idx}
                    series={series}
                    onClick={() => handleSeriesClick(series[0]?.seriesInfo?.seriesInstanceUid)}
                  />
                ))}
              </>
            )}
            {/* If a series is selected, show its instances */}
            {selectedSeriesUid && (
              <>
                <button className="series-back-btn" onClick={handleBackToSeries}>
                  <MdOutlineKeyboardBackspace />
                  Back to Series
                </button>
                {scansToShow.map((scan, index) => (
                  <div className="study-scan" key={index}>
                    <ScanImage thumbnailUrl={scan.image?.thumbnailUrl} />
                    <div className="study-scan__info">
                      <h4 className="study-scan__title">{scan.description}</h4>
                      <div className="study-scan__details">
                        <div className="study-scan__detail-item">
                          <span>Status:</span> Unknown
                        </div>
                        <div className="study-scan__detail-item">
                          <span>Modality:</span> {scan.modality}
                        </div>
                        <div className="study-scan__detail-item">
                          <span>StationName:</span> {scan.stationName}
                        </div>
                        <div className="study-scan__detail-item">
                          <span>ContrastBolusAgent:</span> {scan.contrastBoluAgent}
                        </div>
                        <div className="study-scan__detail-item">
                          <span>BodyPartExamined:</span> {scan.bodyPartExamined}
                        </div>
                        <div className="study-scan__detail-item">
                          <span>ProtocolName:</span> {scan.protocolName}
                        </div>
                        <div className="study-scan__detail-item">
                          <span>SeriesInstanceUID:</span> {scan.seriesInfo?.seriesInstanceUid}
                        </div>
                        <div className="study-scan__detail-item">
                          <span>SeriesNumber:</span> {scan.seriesInfo?.seriesNumber}
                        </div>
                        <div className="study-scan__detail-item">
                          <span>PerformedProcedureStepDescription:</span> {scan.performedProcedureStepDescription}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-wrap mt-auto">
                        <Button size="sm" onClick={() => handleOpenReport(scan.reportId?._id)}>
                          <IoDocumentTextOutline />
                          View Report
                        </Button>
                        {scan.reportId?.chatSessionId && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => history.push(`/?id=${encodeURIComponent(scan.reportId.chatSessionId)}`)}
                          >
                            <IoChatboxEllipsesOutline />
                            Go to Chat
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Study;